<template>
  <div class="recording-button-wrapper">
    <!-- 红色录音按钮 -->
    <button
      type="button"
      class="recording-btn"
      :class="{ 'recording': isRecording, 'processing': isProcessing, 'disabled': isButtonDisabled }"
      :disabled="isButtonDisabled"
      @click="toggleRecord"
      :title="isRecording ? '点击停止录音' : '点击开始录音'">
      <i v-if="isProcessing" class="el-icon-loading"></i>
      <i v-else-if="isRecording" class="el-icon-video-pause"></i>
      <i v-else class="el-icon-microphone"></i>
      <span v-if="isRecording" class="recording-timer">{{ formatTime(recordDuration) }}</span>
    </button>

    <!-- 预览已录制音频的按钮 -->
    <button
      v-if="value && value.url && !isRecording"
      type="button"
      class="preview-btn"
      @click="openPreviewDialog"
      title="预览录音">
      <i class="el-icon-headset"></i>
    </button>

    <!-- 录音确认弹窗 -->
    <el-dialog
      title="录音预览"
      :visible.sync="showDialog"
      width="92%"
      custom-class="mobile-recording-dialog"
      top="8vh"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      class="recording-preview-dialog"
      @close="handleDialogClose">
      <div class="preview-content">
        <!-- 隐藏的audio元素 -->
        <audio
          ref="audioPlayer"
          :src="tempRecordUrl"
          style="display: none;"
          @timeupdate="onTimeUpdate"
          @loadedmetadata="onMetaLoaded"
          @ended="onPlayEnded"></audio>
        
        <!-- 自定义播放器 -->
        <div class="custom-player">
          <!-- 播放按钮 -->
          <div class="play-btn-wrapper" @click="togglePlay">
            <div class="play-btn" :class="{ 'playing': isPlaying }">
              <i v-if="isPlaying" class="el-icon-video-pause"></i>
              <i v-else class="el-icon-video-play"></i>
            </div>
          </div>
          
          <!-- 进度条和时间 -->
          <div class="player-progress">
            <div class="progress-bar" @click="seekTo($event)">
              <div class="progress-fill" :style="{ width: playProgress + '%' }"></div>
              <div class="progress-thumb" :style="{ left: playProgress + '%' }"></div>
            </div>
            <div class="progress-time">
              <span class="time-current">{{ formatTime(playCurrentTime) }}</span>
              <span class="time-total">{{ formatTime(playDuration || tempRecordDuration) }}</span>
            </div>
          </div>
        </div>
        
        <div class="record-info">
          <span>时长: {{ formatTime(tempRecordDuration) }}</span>
          <span>大小: {{ formatSize(tempRecordSize) }}</span>
        </div>

        <!-- 识别结果显示与编辑区 -->
        <div class="recognized-text-wrapper">
          <div class="text-label">
            <i class="el-icon-edit-outline"></i>
            <span>识别结果（可编辑）</span>
          </div>
          <el-input
            v-model="recognizedText"
            type="textarea"
            :rows="3"
            placeholder="未识别到内容，可手动输入"
            resize="none">
          </el-input>
        </div>

        <div class="transcribe-tip">
          <i class="el-icon-info"></i>
          <span>提示：可在上方框中修正识别结果</span>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button class="footer-btn footer-btn--discard" @click="handleDiscard">丢弃重录</el-button>
        <el-button class="footer-btn footer-btn--confirm" type="primary" @click="handleConfirm" :loading="uploading">使用该录音</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
// 使用 @breezystack/lamejs 替代 lamejs，修复 MPEGMode 未定义的问题
import lamejs from '@breezystack/lamejs'
import axios from 'axios'
import { mapGetters } from 'vuex'

/**
 * 构建 TUS 上传元数据
 */
function buildTusMetadata(file) {
  const metadata = {
    filename: btoa(encodeURIComponent(file.name)),
    filetype: btoa(encodeURIComponent(file.type || 'audio/mp3')),
  }
  return Object.entries(metadata)
    .map(([k, v]) => `${k} ${v}`)
    .join(',')
}

export default {
  name: 'AudioRecorder',
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    /** 受控值（录音数据） */
    value: {
      type: Object,
      default: () => ({})
    },
    /** 单段最短时长（秒） */
    minDuration: {
      type: Number,
      default: 1
    },
    /** 单段最长时长（秒） */
    maxDuration: {
      type: Number,
      default: 60
    },
    /** MP3 比特率（kbps） */
    bitRate: {
      type: Number,
      default: 128
    },
    /** 采样率（Hz） */
    sampleRate: {
      type: Number,
      default: 44100
    },
    /** 禁用 */
    disabled: {
      type: Boolean,
      default: false
    },
    /** 只读 */
    readOnly: {
      type: Boolean,
      default: false
    },
    /** 语音识别语言 */
    recognitionLang: {
      type: String,
      default: 'zh-CN'
    }
  },
  data() {
    return {
      // 录音状态
      isRecording: false,
      isProcessing: false,
      recordDuration: 0,
      recordStartTime: 0,

      // 媒体相关
      mediaStream: null,
      audioContext: null,
      sourceNode: null,
      processorNode: null,
      pcmBuffers: [],

      // 定时器
      timerInterval: null,
      maxDurationTimer: null,

      // 波形
      waveTimer: null,

      // 自增 ID
      recordIdCounter: 0,

      // 弹窗相关
      showDialog: false,
      tempRecordUrl: '',
      tempRecordBlob: null,
      tempRecordDuration: 0,
      tempRecordSize: 0,
      uploading: false,

      // 播放状态
      isPlaying: false,
      playProgress: 0,
      playCurrentTime: 0,
      playDuration: 0,
      audioElement: null,

      // TUS 上传地址
      tusEndpoint: '',

      // ===== 语音识别相关 =====
      speechRecognition: null,         // 识别实例
      recognizedText: '',              // 累计识别的最终文字
      interimText: '',                 // 临时识别的文字（边说边出字）
      isRecognitionSupported: false,   // 浏览器是否支持
      manualStop: false                // 是否手动停止（避免自动重启）
    }
  },
  computed: {
    ...mapGetters(['getConfigItem']),
    /** 录音按钮是否禁用 */
    isButtonDisabled() {
      return this.isProcessing || this.disabled || this.readOnly
    }
  },
  beforeDestroy() {
    this.cleanup()
  },
  created() {
    // 获取上传地址配置，与 newUpload.vue 保持一致
    this.tusEndpoint = this.getConfigItem('newUploadLink') || 'https://dcops.test.tencent.com/file-center/tus/relay/cosfile/'

    // 检查浏览器是否支持语音识别
    this.checkSpeechRecognitionSupport()
  },
  methods: {
    /**
     * 触发受控值更新
     */
    emitChange(data) {
      this.$emit('input', data)
      this.$emit('change', data)
    },

    /**
     * 切换录音状态
     */
    async toggleRecord() {
      if (this.isProcessing) return
      if (this.isRecording) {
        await this.stopRecord(false)
      } else {
        await this.startRecord()
      }
    },

    /**
     * 浏览器兼容性检查
     */
    checkBrowserSupport() {
      if (
        window.location.protocol !== 'https:'
        && window.location.hostname !== 'localhost'
        && window.location.hostname !== '127.0.0.1'
        && window.location.hostname !== 'front.woa.com'
      ) {
        return { supported: false, reason: '录音功能需要 HTTPS 环境' }
      }

      const hasModernAPI = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
      const legacyGUM = navigator.getUserMedia
        || navigator.webkitGetUserMedia
        || navigator.mozGetUserMedia

      if (!hasModernAPI && !legacyGUM) {
        return {
          supported: false,
          reason: '当前浏览器不支持录音功能，请使用 Chrome/Edge/Firefox 最新版'
        }
      }

      const AudioContextClass = window.AudioContext || window.webkitAudioContext
      if (!AudioContextClass) {
        return { supported: false, reason: '当前浏览器不支持 AudioContext' }
      }

      return { supported: true }
    },

    /**
     * 兼容获取 getUserMedia
     */
    getUserMediaCompat(constraints) {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        return navigator.mediaDevices.getUserMedia(constraints)
      }

      return new Promise((resolve, reject) => {
        const legacyGUM = navigator.getUserMedia
          || navigator.webkitGetUserMedia
          || navigator.mozGetUserMedia

        if (!legacyGUM) {
          reject(new Error('getUserMedia not supported'))
          return
        }
        legacyGUM.call(navigator, constraints, resolve, reject)
      })
    },

    /**
     * 检查浏览器是否支持 Web Speech API
     */
    checkSpeechRecognitionSupport() {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      this.isRecognitionSupported = !!SpeechRecognition
      if (!this.isRecognitionSupported) {
        console.warn('当前浏览器不支持 Web Speech API，建议使用 Chrome 或 Edge')
      }
    },

    /**
     * 初始化语音识别器
     */
    initSpeechRecognition() {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      if (!SpeechRecognition) {
        return null
      }

      const recognition = new SpeechRecognition()

      // 配置参数
      recognition.continuous = true               // 持续识别
      recognition.interimResults = true           // 返回临时结果（边说边出字）
      recognition.lang = this.recognitionLang     // 识别语言（默认中文）
      recognition.maxAlternatives = 1             // 备选结果数量

      // 识别到结果
      recognition.onresult = (event) => {
        let finalText = ''
        let interimText = ''

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript
          if (event.results[i].isFinal) {
            finalText += transcript
          } else {
            interimText += transcript
          }
        }

        if (finalText) {
          this.recognizedText += finalText
          console.log('✅ 最终识别:', finalText)
        }
        this.interimText = interimText
        if (interimText) {
          console.log('📝 实时识别:', interimText)
        }
      }

      // 识别错误
      recognition.onerror = (event) => {
        console.error('语音识别错误:', event.error)
        if (event.error === 'no-speech') {
          console.warn('未检测到语音')
        } else if (event.error === 'audio-capture') {
          console.warn('无法访问麦克风')
        } else if (event.error === 'not-allowed') {
          console.warn('用户拒绝麦克风权限')
        } else if (event.error === 'network') {
          console.warn('网络错误，Web Speech API 需要联网（Chrome 依赖 Google 服务）')
          this.$message.warning('语音识别需要联网，请检查网络')
        }
      }

      // 识别意外结束时自动重启
      recognition.onend = () => {
        console.log('语音识别结束')
        // 如果还在录音状态且不是手动停止，自动重启识别
        if (this.isRecording && !this.manualStop && this.speechRecognition) {
          try {
            this.speechRecognition.start()
            console.log('🔄 语音识别自动重启')
          } catch (e) {
            console.warn('重启识别失败:', e)
          }
        }
      }

      return recognition
    },

    /**
     * 开始录音
     */
    async startRecord() {
      if (this.disabled || this.readOnly) {
        return
      }

      const check = this.checkBrowserSupport()
      if (!check.supported) {
        this.$message.error(check.reason)
        return
      }

      try {
        this.mediaStream = await this.getUserMediaCompat({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        })

        const AudioContextClass = window.AudioContext || window.webkitAudioContext
        this.audioContext = new AudioContextClass()

        if (this.audioContext.state === 'suspended') {
          await this.audioContext.resume()
        }

        this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream)

        const bufferSize = 4096
        this.processorNode = this.audioContext.createScriptProcessor(bufferSize, 1, 1)
        this.pcmBuffers = []

        this.processorNode.onaudioprocess = (e) => {
          if (!this.isRecording) return
          const inputData = e.inputBuffer.getChannelData(0)
          this.pcmBuffers.push(new Float32Array(inputData))
        }

        this.sourceNode.connect(this.processorNode)
        this.processorNode.connect(this.audioContext.destination)

        this.isRecording = true
        this.recordDuration = 0
        this.recordStartTime = Date.now()

        // ===== 启动语音识别 =====
        this.recognizedText = ''
        this.interimText = ''
        this.manualStop = false

        if (this.isRecognitionSupported) {
          this.speechRecognition = this.initSpeechRecognition()
          if (this.speechRecognition) {
            try {
              this.speechRecognition.start()
              console.log('🎤 语音识别已启动')
            } catch (e) {
              console.warn('启动语音识别失败:', e)
            }
          }
        } else {
          this.$message.warning('当前浏览器不支持实时语音识别，建议使用 Chrome 或 Edge')
        }
        // ===== 语音识别启动结束 =====

        this.timerInterval = setInterval(() => {
          this.recordDuration = (Date.now() - this.recordStartTime) / 1000
        }, 100)

        this.maxDurationTimer = setTimeout(() => {
          if (this.isRecording) {
            this.$message.info(`已达最长录制时长 ${this.maxDuration} 秒，自动停止`)
            this.stopRecord(true)
          }
        }, this.maxDuration * 1000)

        this.$emit('start')
      } catch (err) {
        console.error('启动录音失败:', err)
        let msg = '启动录音失败'
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          msg = '请允许使用麦克风权限'
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          msg = '未检测到麦克风设备'
        } else if (err.name === 'NotReadableError') {
          msg = '麦克风被占用，请关闭其他使用麦克风的程序'
        } else if (err.message) {
          msg = '启动录音失败：' + err.message
        }
        this.$message.error(msg)
        this.$emit('error', err)
        this.cleanup()
      }
    },

    /**
     * 停止录音
     */
    async stopRecord(isAuto = false) {
      if (!this.isRecording) return

      const finalDuration = (Date.now() - this.recordStartTime) / 1000

      // 校验最短时长
      if (finalDuration < this.minDuration) {
        this.$message.warning(`录音时长不足 ${this.minDuration} 秒，已自动丢弃`)
        this.cleanup()
        this.$emit('discard', { duration: finalDuration, reason: 'too-short' })
        return
      }

      this.isRecording = false
      this.isProcessing = true

      if (this.timerInterval) {
        clearInterval(this.timerInterval)
        this.timerInterval = null
      }
      if (this.maxDurationTimer) {
        clearTimeout(this.maxDurationTimer)
        this.maxDurationTimer = null
      }

      // ===== 停止语音识别 =====
      this.manualStop = true
      if (this.speechRecognition) {
        try {
          this.speechRecognition.stop()
          console.log('🛑 语音识别已停止')
        } catch (e) {
          console.warn('停止语音识别失败:', e)
        }
      }
      // ===== 语音识别停止结束 =====

      try {
        const sampleRate = this.audioContext.sampleRate
        const mp3Blob = await this.encodeMp3(this.pcmBuffers, sampleRate)

        const url = URL.createObjectURL(mp3Blob)

        // 保存到临时变量，等待用户确认
        this.tempRecordUrl = url
        this.tempRecordBlob = mp3Blob
        this.tempRecordDuration = finalDuration
        this.tempRecordSize = mp3Blob.size

        // 等待最后的识别结果完成（处理异步延迟）
        await this.waitForFinalRecognition()

        // 显示确认弹窗
        this.showDialog = true

        this.$emit('complete', {
          blob: mp3Blob,
          url,
          duration: finalDuration,
          size: mp3Blob.size,
          isAuto
        })
      } catch (err) {
        console.error('编码失败:', err)
        this.$message.error('录音处理失败')
        this.$emit('error', err)
      } finally {
        this.cleanupStream()
        this.isProcessing = false
        this.recordDuration = 0
      }
    },

    /**
     * 等待最后的识别结果完成（异步处理延迟）
     */
    waitForFinalRecognition() {
      return new Promise((resolve) => {
        // 给识别器 800ms 处理最后的语音
        setTimeout(() => {
          // 把临时结果合并进最终结果
          if (this.interimText) {
            this.recognizedText += this.interimText
            this.interimText = ''
          }
          console.log('🎯 等待识别完成，最终文字:', this.recognizedText)
          resolve()
        }, 800)
      })
    },

    /**
     * 打开预览弹窗（用于查看已保存的录音）
     */
    openPreviewDialog() {
      if (!this.value || !this.value.url) {
        this.$message.warning('没有可预览的录音')
        return
      }

      // 设置临时变量用于预览
      this.tempRecordUrl = this.value.url
      this.tempRecordBlob = null // 已上传的文件不需要blob
      this.tempRecordDuration = 0
      this.recognizedText = this.value.info || ''

      // 显示弹窗
      this.showDialog = true
    },

    /**
     * 用户确认使用录音
     */
    async handleConfirm() {
      this.uploading = true
      try {
        // 1. 上传录音文件到 TUS 服务
        this.$message.info('正在上传录音文件...')
        const audioUrl = await this.uploadToTus(this.tempRecordBlob)
        this.$message.success('录音文件上传成功')

        // 2. 获取前端识别的文字（已在录音过程中完成）
        const info = await this.speechToText(this.tempRecordBlob)

        // 3. 构造输出数据格式
        const result = {
          url: audioUrl,
          info: info || ''
        }

        // 4. 更新字段值
        this.emitChange(result)

        this.showDialog = false
        this.$emit('confirm', result)
      } catch (err) {
        console.error('保存失败:', err)
        this.$message.error('保存失败：' + (err.message || err))
      } finally {
        this.uploading = false
      }
    },

    /**
     * 上传文件到 TUS 服务
     */
    async uploadToTus(blob) {
      const fileName = `recording_${Date.now()}.mp3`
      const file = new File([blob], fileName, { type: 'audio/mp3' })
      const tusUploadUrl = this.tusEndpoint

      // 1. 创建上传会话
      const initResponse = await axios.post(tusUploadUrl, null, {
        headers: {
          'Tus-Resumable': '1.0.0',
          'Upload-Length': file.size,
          'Upload-Metadata': buildTusMetadata(file),
        },
      })

      const uploadLocation = initResponse.headers.location
      if (!uploadLocation) {
        throw new Error('创建上传会话失败：未获取到上传地址')
      }

      // 2. 分块上传文件
      const fullUploadUrl = uploadLocation.startsWith('http')
        ? uploadLocation
        : `${tusUploadUrl}${uploadLocation}`

      let offset = 0
      const chunkSize = 5 * 1024 * 1024 // 5MB 每块

      while (offset < file.size) {
        const chunk = file.slice(offset, offset + chunkSize)
        await axios.patch(fullUploadUrl, chunk, {
          headers: {
            'Content-Type': 'application/offset+octet-stream',
            'Upload-Offset': offset,
            'Tus-Resumable': '1.0.0',
          },
        })
        offset += chunk.size
      }

      // 3. 构造返回的文件URL
      try {
        const fileInfoResponse = await axios.get(fullUploadUrl, {
          headers: {
            'Tus-Resumable': '1.0.0',
          },
        })

        const fileUrl = fileInfoResponse.headers['file-url']
          || fileInfoResponse.data?.url
          || fileInfoResponse.data?.fileUrl

        if (fileUrl) {
          return fileUrl
        }
      } catch (e) {
        console.warn('获取文件信息失败，使用默认URL格式:', e)
      }

      // 降级方案：返回 uploadLocation 作为文件标识
      const uploadId = uploadLocation.split('/').pop()
      return `${tusUploadUrl}${uploadId}`
    },

    /**
     * 语音转文字
     * 使用前端 Web Speech API 实时识别的结果（已在录音过程中完成）
     * 用户在弹窗中可能编辑过 recognizedText，这里直接返回最终值
     */
    async speechToText(blob) {
      const text = (this.recognizedText || '').trim()

      if (!text) {
        console.warn('未识别到任何语音内容')
        return ''
      }

      console.log('🎯 最终使用的识别文字:', text)
      return text
    },

    /**
     * 切换播放/暂停
     */
    togglePlay() {
      const audio = this.$refs.audioPlayer
      if (!audio) return

      if (this.isPlaying) {
        audio.pause()
        this.isPlaying = false
      } else {
        audio.play().then(() => {
          this.isPlaying = true
        }).catch(err => {
          console.error('播放失败:', err)
          this.$message.error('播放失败')
        })
      }
    },

    /**
     * 播放时间更新
     */
    onTimeUpdate() {
      const audio = this.$refs.audioPlayer
      if (!audio) return
      this.playCurrentTime = audio.currentTime
      this.playProgress = (audio.currentTime / audio.duration) * 100 || 0
    },

    /**
     * 音频元数据加载完成
     */
    onMetaLoaded() {
      const audio = this.$refs.audioPlayer
      if (!audio) return
      this.playDuration = audio.duration
    },

    /**
     * 播放结束
     */
    onPlayEnded() {
      this.isPlaying = false
      this.playCurrentTime = 0
      this.playProgress = 0
    },

    /**
     * 点击进度条跳转
     */
    seekTo(event) {
      const audio = this.$refs.audioPlayer
      if (!audio) return
      const progressBar = event.currentTarget
      const rect = progressBar.getBoundingClientRect()
      const percent = (event.clientX - rect.left) / rect.width
      audio.currentTime = percent * audio.duration
    },

    /**
     * 弹窗关闭时停止播放
     */
    handleDialogClose() {
      this.stopPlay()
      // 如果是预览模式（已保存的录音），清空临时数据但不清空value
      if (this.tempRecordBlob === null && this.value && this.value.url) {
        this.tempRecordUrl = ''
      }
    },

    /**
     * 停止播放
     */
    stopPlay() {
      const audio = this.$refs.audioPlayer
      if (audio) {
        audio.pause()
        audio.currentTime = 0
      }
      this.isPlaying = false
      this.playCurrentTime = 0
      this.playProgress = 0
    },

    /**
     * 用户丢弃录音
     */
    handleDiscard() {
      this.stopPlay()
      if (this.tempRecordUrl) {
        URL.revokeObjectURL(this.tempRecordUrl)
      }
      this.tempRecordUrl = ''
      this.tempRecordBlob = null
      this.tempRecordDuration = 0
      this.tempRecordSize = 0
      this.recognizedText = ''
      this.interimText = ''
      this.showDialog = false
      this.$emit('discard', { reason: 'user-cancel' })
    },

    /**
     * PCM -> MP3
     */
    encodeMp3(pcmBuffers, sampleRate) {
      return new Promise((resolve, reject) => {
        try {
          const totalLength = pcmBuffers.reduce((sum, buf) => sum + buf.length, 0)
          const merged = new Float32Array(totalLength)
          let offset = 0
          for (const buf of pcmBuffers) {
            merged.set(buf, offset)
            offset += buf.length
          }

          // Float32 -> Int16
          const int16 = new Int16Array(merged.length)
          for (let i = 0; i < merged.length; i++) {
            const s = Math.max(-1, Math.min(1, merged[i]))
            int16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF
          }

          // 创建 MP3 编码器
          const channels = 1
          const kbps = this.bitRate || 128
          const encoder = new lamejs.Mp3Encoder(channels, sampleRate, kbps)

          const mp3Data = []
          const blockSize = 1152

          // 分批编码
          for (let i = 0; i < int16.length; i += blockSize) {
            const chunk = int16.subarray(i, Math.min(i + blockSize, int16.length))
            const mp3buf = encoder.encodeBuffer(chunk)
            if (mp3buf.length > 0) {
              mp3Data.push(new Uint8Array(mp3buf))
            }
          }

          // 刷新缓冲区
          const endBuf = encoder.flush()
          if (endBuf.length > 0) {
            mp3Data.push(new Uint8Array(endBuf))
          }

          // 合并所有 MP3 数据
          const totalSize = mp3Data.reduce((sum, buf) => sum + buf.length, 0)
          const result = new Uint8Array(totalSize)
          let pos = 0
          for (const buf of mp3Data) {
            result.set(buf, pos)
            pos += buf.length
          }

          resolve(new Blob([result], { type: 'audio/mp3' }))
        } catch (err) {
          console.error('MP3编码错误详情:', err)
          reject(err)
        }
      })
    },

    /**
     * 资源清理 - 媒体流
     */
    cleanupStream() {
      if (this.processorNode) {
        try { this.processorNode.disconnect() } catch (e) {}
        this.processorNode.onaudioprocess = null
        this.processorNode = null
      }
      if (this.sourceNode) {
        try { this.sourceNode.disconnect() } catch (e) {}
        this.sourceNode = null
      }
      if (this.audioContext) {
        try { this.audioContext.close() } catch (e) {}
        this.audioContext = null
      }
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(t => t.stop())
        this.mediaStream = null
      }
      this.pcmBuffers = []
    },

    /**
     * 完整清理
     */
    cleanup() {
      this.isRecording = false
      this.isProcessing = false
      this.recordDuration = 0
      if (this.timerInterval) {
        clearInterval(this.timerInterval)
        this.timerInterval = null
      }
      if (this.maxDurationTimer) {
        clearTimeout(this.maxDurationTimer)
        this.maxDurationTimer = null
      }

      // 清理语音识别器
      this.manualStop = true
      if (this.speechRecognition) {
        try {
          this.speechRecognition.onend = null
          this.speechRecognition.onresult = null
          this.speechRecognition.onerror = null
          this.speechRecognition.stop()
        } catch (e) {}
        this.speechRecognition = null
      }

      this.cleanupStream()
    },

    /**
     * 工具函数 - 格式化时间
     */
    formatTime(seconds) {
      const s = Math.floor(seconds)
      const m = Math.floor(s / 60)
      const sec = s % 60
      return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    },

    /**
     * 工具函数 - 格式化文件大小
     */
    formatSize(bytes) {
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / 1024 / 1024).toFixed(2) + ' MB'
    }
  }
}
</script>

<style scoped lang="scss">
.recording-button-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.recording-btn {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: none;
  outline: none;
  padding: 0;
  background: #f56c6c;
  color: #fff;
  font-size: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  vertical-align: middle;

  &:hover:not(:disabled) {
    background: #e64242;
    transform: scale(1.05);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &.recording {
    background: #e64242;
    animation: recording-pulse 1.5s infinite;

    .recording-timer {
      position: absolute;
      bottom: -22px;
      left: 50%;
      transform: translateX(-50%);
      font-size: 12px;
      color: #f56c6c;
      white-space: nowrap;
      font-family: 'Courier New', monospace;
    }
  }

  &.processing {
    background: #909399;
    cursor: not-allowed;
  }

  &:disabled,
  &.disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .recording-timer {
    display: none;
  }

  &.recording .recording-timer {
    display: block;
  }
}

.preview-btn {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid #dcdfe6;
  outline: none;
  padding: 0;
  background: #fff;
  color: #606266;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  vertical-align: middle;
  margin-left: 8px;

  &:hover {
    background: #f5f7fa;
    color: #409eff;
    border-color: #409eff;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
}

@keyframes recording-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(245, 108, 108, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(245, 108, 108, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(245, 108, 108, 0);
  }
}

/* ========== 移动端弹窗样式 ========== */

// 覆盖 el-dialog 默认样式
::v-deep .mobile-recording-dialog {
  border-radius: 16px;
  margin: 0 auto;
  max-width: 400px;

  .el-dialog__header {
    padding: 16px 20px 10px;
    .el-dialog__title {
      font-size: 17px;
      font-weight: 600;
    }
  }

  .el-dialog__body {
    padding: 10px 20px 16px;
  }

  .el-dialog__footer {
    padding: 0 20px 20px;
  }
}

.preview-content {
  text-align: center;
  padding: 0;

  .record-info {
    display: flex;
    justify-content: center;
    gap: 20px;
    margin-bottom: 12px;
    color: #606266;
    font-size: 14px;
  }

  .transcribe-tip {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    color: #909399;
    font-size: 12px;
    padding: 8px 10px;
    background: #f5f7fa;
    border-radius: 8px;
  }
}

/* 识别结果 */
.recognized-text-wrapper {
  margin: 14px 0;
  text-align: left;

  .text-label {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
    color: #606266;
    font-size: 14px;
    font-weight: 500;

    i {
      color: #409eff;
    }
  }

  ::v-deep .el-textarea__inner {
    border-radius: 8px;
    font-size: 15px;
    line-height: 1.5;
    padding: 10px 12px;
  }
}

/* 自定义播放器 - 移动端适配 */
.custom-player {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 12px;
  margin-bottom: 14px;

  .play-btn-wrapper {
    cursor: pointer;
    flex-shrink: 0;
  }

  .play-btn {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: #f56c6c;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    transition: all 0.3s ease;

    &:active {
      transform: scale(0.92);
    }

    &.playing {
      animation: play-pulse 1.5s infinite;
    }
  }

  .player-progress {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 6px;

    .progress-bar {
      width: 100%;
      height: 6px;
      background: #dcdfe6;
      border-radius: 3px;
      position: relative;
      cursor: pointer;
      // 增大触摸区域
      padding: 8px 0;
      margin: -8px 0;
      overflow: visible;

      .progress-fill {
        height: 6px;
        background: #f56c6c;
        border-radius: 3px;
        transition: width 0.1s linear;
      }

      .progress-thumb {
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
        width: 16px;
        height: 16px;
        background: #f56c6c;
        border-radius: 50%;
        box-shadow: 0 2px 6px rgba(245, 108, 108, 0.4);
        transition: left 0.1s linear;
      }
    }

    .progress-time {
      display: flex;
      justify-content: space-between;
      padding: 0 2px;

      .time-current,
      .time-total {
        font-size: 12px;
        color: #909399;
        font-family: 'Courier New', monospace;
      }
    }
  }
}

/* 底部按钮 - 移动端大按钮 */
.dialog-footer {
  display: flex;
  gap: 12px;
  width: 100%;

  .footer-btn {
    flex: 1;
    height: 44px;
    border-radius: 10px;
    font-size: 16px;
    font-weight: 500;
  }

  .footer-btn--discard {
    border-color: #dcdfe6;
    color: #606266;
    background: #fff;

    &:active {
      background: #f5f7fa;
    }
  }

  .footer-btn--confirm {
    background: #f56c6c;
    border-color: #f56c6c;

    &:active {
      background: #e64242;
    }
  }
}

@keyframes play-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(245, 108, 108, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(245, 108, 108, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(245, 108, 108, 0);
  }
}
</style>